/* eslint-disable @typescript-eslint/no-explicit-any */
/** 对象工具函数 */

/**
 * 递归函数：将嵌套对象转换为扁平的kebab-case属性
 *
 * @param obj 要转换的对象
 * @param prefix 前缀
 * @returns 扁平化的kebab-case对象
 */
export const flattenObjectToKebabCase = (
  obj: Record<string, unknown>,
  prefix = "",
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const kebabKey = key.replace(
      /[A-Z]/g,
      (match) => `-${match.toLowerCase()}`,
    );
    const fullKey = prefix ? `${prefix}-${kebabKey}` : kebabKey;

    if (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      // 检查是否为纯对象（排除Date、RegExp等特殊对象）
      Object.prototype.toString.call(value) === "[object Object]"
    ) {
      // 递归处理嵌套对象
      const nestedResult = flattenObjectToKebabCase(
        value as Record<string, unknown>,
        fullKey,
      );
      Object.assign(result, nestedResult);
    } else {
      // 基本类型或数组直接赋值
      result[fullKey] = value;
    }
  });

  return result;
};

/**
 * 将对象的属性转换为kebab-case命名，支持多层嵌套对象
 *
 * @param props 要转换的属性对象
 * @param excludeKeys 要排除的属性键名数组
 * @returns 转换后的kebab-case属性对象
 */
export const convertPropsToKebabCase = (
  props: Record<string, unknown>,
  excludeKeys: string[] = ["children"],
): Record<string, unknown> => {
  return Object.keys(props).reduce((acc: Record<string, unknown>, key) => {
    // 排除指定的属性
    if (!excludeKeys.includes(key)) {
      const propValue = props[key];

      // 如果属性值是对象且不是数组，则使用递归函数展开
      if (
        typeof propValue === "object" &&
        propValue !== null &&
        !Array.isArray(propValue) &&
        // 检查是否为纯对象
        Object.prototype.toString.call(propValue) === "[object Object]"
      ) {
        const kebabMainKey = key.replace(
          /[A-Z]/g,
          (match) => `-${match.toLowerCase()}`,
        );
        const flattenedProps = flattenObjectToKebabCase(
          propValue as Record<string, unknown>,
          kebabMainKey,
        );
        Object.assign(acc, flattenedProps);
      } else {
        // 普通属性直接转换
        const kebabKey: string = key.replace(
          /[A-Z]/g,
          (match) => `-${match.toLowerCase()}`,
        );
        acc[kebabKey] = propValue;
      }
    }

    return acc;
  }, {});
};

/**
 * 将字符串转换为Kebab-case格式
 *
 * @param str 要转换的字符串
 * @returns Kebab-case格式的字符串
 */
export const toKebabCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
};

/**
 * 判断两个对象是否深度相等
 *
 * @param obj0
 * @param obj1
 * @returns
 */
export const isObjectEqual = (obj0: any, obj1: any): boolean => {
  if (obj0 === obj1) return true;

  if (
    typeof obj0 !== "object" ||
    obj0 === null ||
    typeof obj1 !== "object" ||
    obj1 === null
  ) {
    return false;
  }

  const keys0 = Object.keys(obj0);
  const keys1 = Object.keys(obj1);

  if (keys0.length !== keys1.length) return false;

  for (const key of keys0) {
    if (!keys1.includes(key) || !isObjectEqual(obj0[key], obj1[key])) {
      return false;
    }
  }
  return true;
};

/**
 * 返回一个新对象，其所有字符串字段值都经过 trim() 处理（去除前后空白字符）。 非字符串类型的字段保持不变。
 *
 * @example
 *   const formInfo = { name: "  Alice  ", age: 25, note: "  Hi! " };
 *   const cleaned = trimObjectStrings(formInfo);
 *   cleaned = { name: "Alice", age: 25, note: "Hi!" };
 *
 * @param {Object} obj - 原始对象，字段值可以是任意类型。
 * @returns {Object} 返回一个新对象，所有字符串字段值已去除前后空白。
 */
export const trimObjectStrings = (obj: Record<string, any>) => {
  const result = {};
  for (const key in obj) {
    const value = obj[key];
    if (typeof value === "string") {
      (result as Record<string, any>)[key] = value.trim();
    } else {
      (result as Record<string, any>)[key] = value;
    }
  }
  return result;
};
